import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@core/api/baseQuery';
import type { Invitation, Membership, OrganizationRole, PaginatedResponse } from '@core/types/api';

/** How a seat's access is written: every company, or the ones named by uuid. */
export interface CompanyGrant {
  all_companies: boolean;
  companies: string[];
}

export interface UpdateMemberArgs extends Partial<CompanyGrant> {
  uuid: string;
  role?: OrganizationRole;
}

export interface CreateInvitationArgs extends CompanyGrant {
  email: string;
  role: OrganizationRole;
}

/** The roster is read whole and filtered in the browser: a centre has tens of seats, not thousands. */
const ROSTER_PAGE_SIZE = 200;

/**
 * Team API: who holds a seat in the active organization, and who has been invited
 * to one.
 *
 * TENANT-scoped (never company-scoped): a seat belongs to the organization and names
 * the companies it reaches, so the list is the same whichever company is active. Changing
 * a seat's companies changes what the company switcher may offer that person, which is
 * why the store invalidates the company list when a grant is refused.
 */
export const teamApi = createApi({
  reducerPath: 'teamApi',
  baseQuery,
  tagTypes: ['Member', 'Invitation'],
  endpoints: (builder) => ({
    listMembers: builder.query<Membership[], void>({
      query: () => ({
        url: '/api/accounts/members/',
        method: 'GET',
        params: { page_size: ROSTER_PAGE_SIZE, ordering: 'joined_at' },
      }),
      transformResponse: (response: PaginatedResponse<Membership>) => response?.results ?? [],
      providesTags: ['Member'],
    }),

    updateMember: builder.mutation<Membership, UpdateMemberArgs>({
      query: ({ uuid, ...body }) => ({
        url: `/api/accounts/members/${uuid}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Member'],
    }),

    removeMember: builder.mutation<void, string>({
      query: (uuid) => ({ url: `/api/accounts/members/${uuid}/`, method: 'DELETE' }),
      invalidatesTags: ['Member'],
    }),

    listInvitations: builder.query<Invitation[], void>({
      query: () => ({ url: '/api/accounts/invitations/', method: 'GET' }),
      transformResponse: (response: Invitation[] | PaginatedResponse<Invitation>) =>
        Array.isArray(response) ? response : (response?.results ?? []),
      providesTags: ['Invitation'],
    }),

    createInvitation: builder.mutation<Invitation, CreateInvitationArgs>({
      query: (body) => ({ url: '/api/accounts/invitations/', method: 'POST', body }),
      invalidatesTags: ['Invitation'],
    }),

    resendInvitation: builder.mutation<Invitation, string>({
      query: (uuid) => ({ url: `/api/accounts/invitations/${uuid}/resend/`, method: 'POST' }),
      invalidatesTags: ['Invitation'],
    }),

    revokeInvitation: builder.mutation<void, string>({
      query: (uuid) => ({ url: `/api/accounts/invitations/${uuid}/`, method: 'DELETE' }),
      invalidatesTags: ['Invitation'],
    }),
  }),
});

export const {
  useListMembersQuery,
  useUpdateMemberMutation,
  useRemoveMemberMutation,
  useListInvitationsQuery,
  useCreateInvitationMutation,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
} = teamApi;
